import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Form, Table, Spinner, Alert } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";

async function authGetJson(url) {
  const r = await authFetch(url);

  // deja JSON (în proiectul tău, authFetch uneori returnează direct obiect)
  if (r && typeof r === "object" && !("ok" in r) && !("json" in r)) return r;

  if (r?.ok) return await r.json();

  const msg = r?.text ? await r.text() : "Request failed";
  throw new Error(msg);
}

function normalizeItems(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

// ✅ GARANȚIE: orice rând selectat devine { id, label, cod?, denumire?, raw }
function toOption(it) {
  const id = it?.id ?? it?.bxid ?? it?.tip_factura_intrare_id;
  const cod = it?.cod ?? it?.bxcod ?? "";
  const den =
    it?.denumire ?? it?.name ?? it?.bxdenumire ?? it?.tip_factura ?? "";

  const label =
    it?.label || (cod && den ? `${cod} • ${den}` : den || cod || `#${id}`);

  return { id: Number(id), label: Number(id) === 1 ? "Normală" : String(label), cod, denumire: den, raw: it };
}

export default function TipFacturaIntrarePickerModal({
  show,
  onHide,
  onPick,
  title = "Alege clasificarea fiscală",
  excludeDocumentKinds = false,
  initialQuery = "",
  minChars = 0,
}) {
  const [q, setQ] = useState(initialQuery || "");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const inputRef = useRef(null);

  // focus pe search când se deschide
  useEffect(() => {
    if (show) {
      setErr("");
      setTimeout(() => inputRef.current?.focus?.(), 50);
    }
  }, [show]);

  const canSearch = useMemo(
    () => (q || "").trim().length >= minChars,
    [q, minChars],
  );

  const load = async () => {
    const qq = (q || "").trim();
    if (!canSearch) {
      setRows([]);
      return;
    }

    setLoading(true);
    setErr("");
    try {
      // ✅ endpoint: adaptează dacă e altul
      const data = await authGetJson(
        `/tip_factura_intrare/?skip=0&limit=50&q=${encodeURIComponent(qq)}`,
      );
      const items = normalizeItems(data);
      setRows(excludeDocumentKinds ? items.filter(it => ![2, 8].includes(toOption(it).id)) : items);
    } catch (e) {
      setErr(e?.message || "Eroare la încărcare");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // încărcare automată când se deschide (dacă minChars permite)
  useEffect(() => {
    if (!show) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const handlePick = (it) => {
    const opt = toOption(it);

    // ✅ AICI e “tip-safe”: onPick primește mereu id+label
    onPick(opt);

    onHide?.();
  };

  return (
    <Modal show={!!show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group className="mb-2">
          <Form.Label>Caută</Form.Label>
          <div className="d-flex gap-2">
            <Form.Control
              ref={inputRef}
              value={q}
              placeholder="Scrie denumire/cod..."
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  load();
                }
              }}
            />
            <Button
              variant="primary"
              onClick={load}
              disabled={!canSearch || loading}
            >
              Caută
            </Button>
          </div>

          {minChars > 0 && !canSearch ? (
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>
              Introdu minim {minChars} caractere.
            </div>
          ) : null}
        </Form.Group>

        {err ? <Alert variant="danger">{err}</Alert> : null}
        {loading ? <Spinner animation="border" size="sm" /> : null}

        <Table hover size="sm" responsive className="mt-2">
          <thead>
            <tr>
              <th style={{ width: 90 }}>ID</th>
              <th>Clasificare fiscală</th>
              <th style={{ width: 120 }} />
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((it) => {
              const opt = toOption(it);
              return (
                <tr key={opt.id}>
                  <td>{opt.id}</td>
                  <td>{opt.label}</td>
                  <td className="text-end">
                    <Button size="sm" onClick={() => handlePick(it)}>
                      Alege
                    </Button>
                  </td>
                </tr>
              );
            })}

            {!loading && (rows || []).length === 0 ? (
              <tr>
                <td colSpan={3} className="text-muted">
                  Niciun rezultat.
                </td>
              </tr>
            ) : null}
          </tbody>
        </Table>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
